import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'
import {
  AlertCircle,
  Heart,
  Activity,
  TrendingUp,
  TrendingDown,
  Thermometer,
  Coffee,
  Info,
} from 'lucide-react'
import { useMarkNotificationAsReadMutation } from '@/store'
import type { Notification, NotificationType } from '@/types'
import { NOTIFICATION_PRIORITY } from '@/constants'

interface NotificationListProps {
  notifications: Notification[]
  onClose?: () => void
}

const notificationIcons: Record<NotificationType, React.ReactNode> = {
  estrus_detected: <Heart className="w-5 h-5" />,
  behavior_change: <Activity className="w-5 h-5" />,
  health_alert: <AlertCircle className="w-5 h-5" />,
  low_activity: <TrendingDown className="w-5 h-5" />,
  high_activity: <TrendingUp className="w-5 h-5" />,
  temperature_alert: <Thermometer className="w-5 h-5" />,
  feeding_alert: <Coffee className="w-5 h-5" />,
  general: <Info className="w-5 h-5" />,
}

const notificationColors: Record<NotificationType, string> = {
  estrus_detected: 'text-pink-600 bg-pink-100',
  behavior_change: 'text-blue-600 bg-blue-100',
  health_alert: 'text-error bg-error-light',
  low_activity: 'text-orange-600 bg-orange-100',
  high_activity: 'text-green-600 bg-green-100',
  temperature_alert: 'text-red-600 bg-red-100',
  feeding_alert: 'text-yellow-600 bg-yellow-100',
  general: 'text-text-secondary bg-background-secondary',
}

const priorityColors = {
  low: 'border-l-4 border-l-text-tertiary',
  medium: 'border-l-4 border-l-warning',
  high: 'border-l-4 border-l-error',
  critical: 'border-l-4 border-l-error bg-error-light/20',
}

export function NotificationList({ notifications, onClose }: NotificationListProps) {
  const [markAsRead] = useMarkNotificationAsReadMutation()

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (notification.status === 'unread') {
      try {
        await markAsRead(notification.id).unwrap()
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    }

    // Navigate if there's an action URL
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl
      onClose?.()
    }
  }

  return (
    <div className="divide-y divide-border-light">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          onClick={() => handleNotificationClick(notification)}
          className={`
            p-4 hover:bg-background-secondary transition-colors cursor-pointer
            ${notification.status === 'unread' ? 'bg-primary-50' : ''}
            ${priorityColors[notification.priority]}
          `}
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div
              className={`
                flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                ${notificationColors[notification.type]}
              `}
            >
              {notificationIcons[notification.type]}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="text-sm font-semibold text-text-primary">
                  {notification.title}
                </h4>
                <span className="flex-shrink-0 text-xs text-text-tertiary">
                  {formatDistanceToNow(new Date(notification.triggerTime), {
                    addSuffix: true,
                    locale: th,
                  })}
                </span>
              </div>

              <p className="text-sm text-text-secondary line-clamp-2 mb-2">
                {notification.message}
              </p>

              {/* Metadata */}
              <div className="flex items-center gap-3 text-xs text-text-tertiary">
                {notification.cowCode && (
                  <span className="inline-flex items-center gap-1">
                    <span className="font-medium">โค:</span>
                    {notification.cowName || notification.cowCode}
                  </span>
                )}
                {notification.farmName && (
                  <span className="inline-flex items-center gap-1">
                    <span className="font-medium">ฟาร์ม:</span>
                    {notification.farmName}
                  </span>
                )}
                <span
                  className={`
                    px-2 py-0.5 rounded-full text-xs font-medium
                    ${
                      notification.priority === 'critical'
                        ? 'bg-error text-white'
                        : notification.priority === 'high'
                        ? 'bg-warning text-white'
                        : notification.priority === 'medium'
                        ? 'bg-info text-white'
                        : 'bg-background-gray text-text-secondary'
                    }
                  `}
                >
                  {NOTIFICATION_PRIORITY[notification.priority]}
                </span>
              </div>

              {/* Action button */}
              {notification.actionRequired && notification.actionLabel && (
                <button
                  className="mt-2 text-xs text-primary-800 hover:text-primary-900 font-medium"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleNotificationClick(notification)
                  }}
                >
                  {notification.actionLabel} →
                </button>
              )}
            </div>

            {/* Unread indicator */}
            {notification.status === 'unread' && (
              <div className="flex-shrink-0 w-2 h-2 bg-primary-800 rounded-full mt-1" />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
